/**
 * [DUBAI 2026-10-19, user] "Flight to Dubai" — the five pages of the Dubai contest board, the look the user chose
 * (10-19: "i am liking the first option"). A straight port of the approved preview's LOOK1
 * (.claude/tools/dubai-contest/dubai-contest-looks.html): each page writes its markup into the board element and runs
 * its own motion on ONE requestAnimationFrame loop, which the shell stops before the next page is drawn.
 *
 * ⚠ Every value that reaches the markup goes through esc() (text) or money() (numbers) — names, websites and reasons
 * come from HubSpot and must never be read as HTML.
 * ⚠ Keyframe names carry a `dxb-` prefix (boardStyles.ts) because they are global; inline animations below use them too.
 */
import {
  Model, DubaiDeal, Ranked, CATS, RULES, esc, money, plural, r2, daysLeft, dubaiTime,
} from './model';

export type PageId = 'standings' | 'scorecard' | 'chase' | 'voice' | 'deal';
export interface Loop { add(fn: (now: number) => boolean): void; every(ms: number, fn: () => void): void; after(ms: number, fn: () => void): void; stop(): void }
export interface DealOpts { deal: DubaiDeal | null; fresh: boolean; test: boolean; gifUrl: string | null }

export const REDUCED = typeof window !== 'undefined' && !!window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ─────────────────────────────────────────────────────────── shared parts ── */

const mixTotal = (p: Ranked): number => CATS.reduce((a, [k]) => a + (p.m[k] || 0), 0);
const mixBar = (p: Ranked): string => {
  const tot = mixTotal(p);
  if (!tot) return '<div class="mix"></div>';
  return '<div class="mix">' + CATS.filter(([k]) => p.m[k] > 0)
    .map(([k]) => `<i class="${k}" style="width:${((p.m[k] / tot) * 100).toFixed(2)}%"></i>`).join('') + '</div>';
};
const legendHTML = (): string => `<div class="legend">${CATS.map(([k, l, w]) => `<span style="--dot: var(--cat-${k})">${l} ${w}</span>`).join('')}</div>`;
const restChips = (M: Model, from: number, to: number): string => M.scored.slice(from, to)
  .map((p, i) => `<span class="chip" style="--i:${i + 1}">${from + i + 1} ${esc(p.n)}<em class="tnum">${money(p.s, false)}</em></span>`).join('');
/**
 * [DUBAI-FILL 2026-10-19, user] "it should cover the complete page … make the dashboard complete on the sides also": the canvas
 * is 900 tall and as WIDE as the screen's shape asks for (1600 on a 16:9 TV — the approved layout, untouched — up to 2200 on a
 * wide window; DubaiContestPage sets data-cw). Left-hand parts keep their place, the right-hand column pins to the right edge, and
 * the route map, runway, tables and chips take the extra width. Read it here, in CANVAS px.
 */
const cwOf = (el: HTMLElement): number => Math.max(1600, Math.round(Number(el.dataset.cw) || 1600));
const starField = (count: number, maxY: number, seed0: number, width = 1600): string => {
  let seed = seed0;
  const rnd = (): number => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  let out = '';
  for (let i = 0; i < count; i += 1) {
    const x = rnd() * width; const y = rnd() * maxY; const r = rnd() * 1.3 + 0.3; const d = rnd() * 6; const o = 0.2 + rnd() * 0.45 * (1 - y / maxY);
    out += i % 4 === 0
      ? `<circle class="tw" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(2)}" style="animation-delay:${d.toFixed(2)}s"></circle>`
      : `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(2)}" opacity="${o.toFixed(2)}"></circle>`;
  }
  return out;
};

/* ─────────────────────────────────────────────────────────── motion ── */

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));
const easeOut = (x: number): number => 1 - Math.pow(1 - x, 3);
const easeInOut = (x: number): number => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

/** One requestAnimationFrame loop per page, plus its timers — all stopped together when the page leaves. */
export function startLoop(): Loop {
  let raf = 0;
  const list: ((now: number) => boolean)[] = [];
  const timers: number[] = [];
  const tick = (now: number): void => {
    for (let i = list.length - 1; i >= 0; i -= 1) if (list[i](now)) list.splice(i, 1);
    raf = list.length ? requestAnimationFrame(tick) : 0;
  };
  return {
    add(fn) { list.push(fn); if (!raf) raf = requestAnimationFrame(tick); },
    every(ms, fn) { timers.push(window.setInterval(fn, ms)); },
    after(ms, fn) { timers.push(window.setTimeout(fn, ms)); },
    stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      list.length = 0;
      timers.forEach((tm) => { window.clearInterval(tm); window.clearTimeout(tm); });
      timers.length = 0;
    },
  };
}
/** Numbers count up from zero. The final figure is already in the markup, so a still frame shows real values. */
function countUp(root: HTMLElement, loop: Loop): void {
  if (REDUCED) return;
  const els = Array.from(root.querySelectorAll<HTMLElement>('[data-count]')).map((e) => ({
    e, v: Number(e.dataset.count), cents: e.dataset.cents !== '0', delay: Number(e.dataset.delay || 0), pre: e.dataset.pre || '',
  }));
  let start = 0;
  loop.add((now) => {
    if (!start) start = now;
    let done = true;
    els.forEach((x) => {
      const p = clamp01((now - start - x.delay) / 1300);
      if (p < 1) done = false;
      const txt = x.pre + money(x.v * easeOut(p), x.cents);
      if (x.e.textContent !== txt) x.e.textContent = txt;
    });
    return done;
  });
}
/* Airport split-flap: characters spin through random letters and digits, then settle left to right. */
/** [DUBAI-CHASE-20 2026-10-19, user] "every 20 seconds it will have the animation and the data for all will be changed … on the
 *  chase tab visit it will cover all the names and then again when we come to this the chase tab then it will cover all the
 *  names": a Chase visit is one minute, and every 20 s the next group of cards + planes comes in with the full animation, so
 *  ONE visit shows every name (today 3 groups: ranks 2–11, 12–21, 22–24) and the next visit starts again from the first
 *  group. (Was [DUBAI-REPLAY]: the next group every 30 s, a visit showed two and the next visit carried on.) More groups than
 *  fit in the minute at 20 s → the minute is split evenly, so every name still comes up in the visit. */
export const CHASE_VISIT_MS = 60000;
const CHASE_STEP_MS = 20000;
/** The visit's own clock: how long THIS visit has had the screen. A redraw (new figures, a new width) keeps the group on
 *  screen and its timing; a visit that a new deal interrupted carries on where it stopped ([DUBAI-RESUME]); chaseVisit() —
 *  called by the circuit when a visit starts — begins again from the first group. */
let chaseShown = 0;
let chaseFrom = 0;
let chaseFresh = true;
export function chaseVisit(): void { chaseFresh = true; }
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const spinChar = (ch: string): string => {
  if (/[A-Z]/.test(ch)) return LETTERS[Math.floor(Math.random() * 26)];
  if (/[a-z]/.test(ch)) return LETTERS[Math.floor(Math.random() * 26)].toLowerCase();
  if (/[0-9]/.test(ch)) return String(Math.floor(Math.random() * 10));
  return ch;
};
interface FlapCell { e: HTMLElement; text: string; row: number; col: number }
function flapFrame(cells: FlapCell[], base: number, blankFirst: boolean): (now: number) => boolean {
  const DUR = 560; const STEP = 55;
  let start = 0;
  const last = new Map<FlapCell, number>();
  return (now) => {
    if (!start) start = now;
    let done = true;
    cells.forEach((c) => {
      const tt = now - start - (base + c.row * 70 + c.col * 40);
      if (tt < 0) { done = false; if (blankFirst && c.e.textContent !== '') c.e.textContent = ''; return; }
      if (tt >= DUR) { if (c.e.textContent !== c.text) c.e.textContent = c.text; return; }
      done = false;
      const step = Math.floor(tt / STEP);
      if (last.get(c) === step) return;
      last.set(c, step);
      const reveal = Math.floor(c.text.length * (tt / DUR));
      c.e.textContent = Array.from(c.text).map((ch, k) => (k < reveal ? ch : spinChar(ch))).join('');
    });
    return done;
  };
}
const flapCells = (root: HTMLElement, sel: string): FlapCell[] => Array.from(root.querySelectorAll<HTMLElement>(sel))
  .map((e) => ({ e, text: e.dataset.text || '', row: Number(e.dataset.row || 0), col: Number(e.dataset.col || 0) }));
/** An endlessly rolling list: the rows are rendered twice and the track moves by exactly one copy, so the loop is seamless. */
function startRoll(scroller: HTMLElement | null, pxPerSec: number): void {
  if (!scroller) return;
  const track = scroller.querySelector<HTMLElement>('.track');
  if (!track) return;
  const half = track.scrollHeight / 2;
  if (REDUCED || half <= scroller.clientHeight) { track.querySelectorAll('.dup').forEach((n) => n.remove()); return; }
  scroller.style.setProperty('--dur', `${(half / pxPerSec).toFixed(1)}s`);
  scroller.classList.add('roll');
}

/* ─────────────────────────────────────────────────────────── Flight to Dubai ── */

const PLANE = 'M0 -11 L2 -4 L11 2 L11 4 L2 2 L1.5 8 L4.5 10 L4.5 11.5 L0 10.5 L-4.5 11.5 L-4.5 10 L-1.5 8 L-2 2 L-11 4 L-11 2 L-2 -4 Z';
/** The plane the page change carries across the screen (shell: the swoosh band). */
export const PLANE_SVG = `<svg class="plane" viewBox="-12 -12 24 24" aria-hidden="true"><path transform="rotate(90)" fill="#ffb547" d="${PLANE}"></path></svg>`;
const hl = (s: string): string => esc(s).replace(/(\$[\d,]+\.\d{2}|\d+(?:\.\d)?%)/g, '<b>$1</b>');
const trailDefs = `<defs>
    <linearGradient id="l1trail" gradientUnits="userSpaceOnUse" x1="0" y1="13" x2="0" y2="70"><stop offset="0" stop-color="#eaf1f7" stop-opacity=".6"></stop><stop offset="1" stop-color="#eaf1f7" stop-opacity="0"></stop></linearGradient>
    <linearGradient id="l1trailLead" gradientUnits="userSpaceOnUse" x1="0" y1="13" x2="0" y2="70"><stop offset="0" stop-color="#ffb547" stop-opacity=".85"></stop><stop offset="1" stop-color="#ffb547" stop-opacity="0"></stop></linearGradient></defs>`;
const sky = (W: number): string => `<svg class="abs stars" viewBox="0 0 ${W} 900" style="width:${W}px" aria-hidden="true">${starField(Math.round((120 * W) / 1600), 600, 7, W)}</svg>
    <div class="shoot" style="left:1280px; top:70px" aria-hidden="true"></div><div class="shoot s2" style="left:930px; top:40px" aria-hidden="true"></div>`;
const info = (): string => `<div class="abs l1-info"><div class="n tnum" data-days>${daysLeft()}</div><div class="u">Days left</div><div class="s clock">Dubai time <b data-clock></b></div></div>`;
const pageHead = (title: string, take: string): string => `<header class="abs l1-ph"><div class="eyebrow">The Scale Agency · GITEX GLOBAL Dubai Contest</div>
    <h2 class="l1-title">${esc(title)}</h2><div class="l1-take">${hl(take)}</div></header>`;
/* [DUBAI-FOOT 2026-10-19, user] The foot holds ONE line, on the RIGHT ("on the bottom left side whatever the line is written,
   make it to the bottom right side"); the "Live data, read …" note is gone ("remove this line"). */
const pageFoot = (_M: Model, withLegend = true): string =>
  `<div class="abs l1-pf">${withLegend ? legendHTML() : `<span>${esc(RULES)}</span>`}</div>`;
function clock(el: HTMLElement, loop: Loop): void {
  // [DUBAI-GST 2026-10-19, user] the zone after the time, as IST is for India: Dubai = GST (Gulf Standard Time).
  // The element is looked up on every tick: a page that paints itself again ([DUBAI-REPLAY]) replaces it.
  const set = (): void => {
    const c = el.querySelector<HTMLElement>('[data-clock]');
    if (!c) return;
    const tm = dubaiTime(); const [h, m] = tm.split(':');
    const next = tm ? `${h}<i>:</i>${m} GST` : '';
    if (c.innerHTML !== next) c.innerHTML = next;
  };
  set();
  loop.every(10000, set);
}
const empty = (el: HTMLElement, M: Model, title: string): Loop => {
  el.innerHTML = `${sky(cwOf(el))}${pageHead(title, 'The first deal of the contest is still to come')}${info()}${pageFoot(M)}`;
  const loop = startLoop();
  clock(el, loop);
  return loop;
};

/* 1 · Standings — the route to DXB, the departures board, the boarding pass */
function standings(el: HTMLElement, M: Model): Loop {
  const LEAD = M.lead; const SECOND = M.second;
  if (!LEAD) return empty(el, M, 'Earn your spot to Dubai');
  // [DUBAI-FILL] half the extra width goes to the route (and the boarding pass under it), half to the departures board
  const W = cwOf(el); const half = (W - 1600) / 2; const mapW = 880 + half;
  const sx = (x: number): number => 70 + ((x - 70) * (mapW - 150)) / 730;
  const P = [[70, 330], [sx(250), 120], [sx(560), 40], [mapW - 80, 70]];
  const route = `M70 330 C ${P[1][0].toFixed(1)} 120, ${P[2][0].toFixed(1)} 40, ${P[3][0].toFixed(1)} 70`;
  const at = (tt: number): [number, number] => {
    const u = 1 - tt; const a = u * u * u; const b = 3 * u * u * tt; const c = 3 * u * tt * tt; const d = tt * tt * tt;
    return [a * P[0][0] + b * P[1][0] + c * P[2][0] + d * P[3][0], a * P[0][1] + b * P[1][1] + c * P[2][1] + d * P[3][1]];
  };
  const slope = (tt: number): number => {
    const u = 1 - tt;
    const dx = 3 * u * u * (P[1][0] - P[0][0]) + 6 * u * tt * (P[2][0] - P[1][0]) + 3 * tt * tt * (P[3][0] - P[2][0]);
    const dy = 3 * u * u * (P[1][1] - P[0][1]) + 6 * u * tt * (P[2][1] - P[1][1]) + 3 * tt * tt * (P[3][1] - P[2][1]);
    return Math.atan2(dy, dx);
  };
  // [DUBAI-10-PLANES 2026-10-19, user] "this should have 10 planes, because for the table on the right we are showing top 10":
  // one plane per row of the departures board. Lanes alternate sides of the route so close scores (#9 and #10 are $12.50
  // apart today) sit side by side instead of on top of each other.
  const LANES = [0, 32, -32, 64, -64, 32, -32, 64, -64, 0];
  const PL = M.scored.slice(0, 10).map((p, i) => ({ i, target: 0.08 + 0.84 * (p.s / LEAD.s) }));
  const planeSvg = PL.map(({ i }) => `<g class="pl" id="l1pl${i}"><g class="bob" style="animation-delay:${(i * 0.45).toFixed(2)}s">
      <g class="rot"><line class="trail" x1="0" y1="13" x2="0" y2="70" stroke="url(#${i === 0 ? 'l1trailLead' : 'l1trail'})"></line><path class="plane${i === 0 ? ' lead' : ''}" transform="scale(1.15)" d="${PLANE}"></path></g>
      <g class="tag${i === 0 ? ' lead' : ''}"><circle r="11"></circle><text>${i + 1}</text></g></g></g>`).join('');
  const cell = (cls: string, text: string, row: number, col: number): string =>
    `<div class="cell ${cls}"><span data-text="${esc(text)}" data-row="${row}" data-col="${col}">${esc(text)}</span></div>`;
  const rows = M.scored.slice(0, 10).map((p, i) => `<div class="row flip${i === 0 ? ' lead' : ''}" style="--fd:${300 + i * 85}ms">
      ${cell('flap rank tnum', String(i + 1), i, 0)}
      <div class="cell name"><span data-text="${esc(p.n)}" data-row="${i}" data-col="1">${esc(p.n)}</span>${mixBar(p)}</div>
      ${cell('flap team', p.t, i, 2)}${cell('flap score r tnum', money(p.s), i, 3)}
      ${cell('flap gap r tnum', i === 0 ? 'LEADER' : '−' + money(LEAD.s - p.s), i, 4)}</div>`).join('');
  const days = String(daysLeft());
  const ahead = SECOND ? r2(LEAD.s - SECOND.s) : LEAD.s;
  const html = `${sky(W)}
    <header class="abs l1-head"><div class="eyebrow">The Scale Agency · GITEX GLOBAL Dubai Contest</div>
      <h2 class="l1-title">Earn your spot to Dubai</h2>
      <div class="l1-sub">Highest weighted recurring revenue wins · 5 October – 30 November 2026</div>
      <div class="l1-take">${hl(M.take.standings)}</div></header>
    <div class="abs l1-count"><div class="n tnum" data-days data-text="${days}" data-row="0" data-col="0">${days}</div><div class="u">Days left</div>
      <div class="s">GITEX GLOBAL · 7–11 Dec 2026, Dubai</div><div class="s clock">Dubai time <b data-clock></b></div></div>
    <svg class="abs l1-map" viewBox="0 0 ${mapW} 380" style="width:${mapW}px" aria-hidden="true">${trailDefs}
      <path class="route-bg" d="${route}"></path><path class="route" d="${route}"></path>
      <circle class="dot-start" cx="70" cy="330" r="7"></circle><text class="lbl" x="70" y="364" text-anchor="middle">START · 5 OCT</text>
      <g transform="translate(${mapW - 880} 0)">
      <path class="burj" d="M800 6 L802 22 L804 34 L806 44 L808 53 L810 60 L790 60 L792 53 L794 44 L796 34 L798 22 Z"></path><circle class="burj-light" cx="800" cy="6" r="2.6"></circle>
      <circle class="pulse" cx="800" cy="70" r="10"></circle><circle class="pulse p2" cx="800" cy="70" r="10"></circle><circle class="dot-dxb" cx="800" cy="70" r="6"></circle>
      <text class="lbl dxb" x="800" y="-8" text-anchor="middle">DXB · DUBAI</text></g>${planeSvg}</svg>
    <div class="abs l1-board" style="left:auto; right:48px; width:${622 + half}px"><div class="hdr"><span>#</span><span>Name</span><span>Team</span><span class="r">Score</span><span class="r">To 1st</span></div>${rows}</div>
    <div class="abs l1-pass" style="width:${860 + half}px"><div class="main"><div class="kicker">Boarding pass · leader today</div><div class="pax">${esc(LEAD.n)}</div>
        <div class="fields"><div class="f"><b>Team</b><span>${esc(LEAD.t)}</span></div><div class="f"><b>Score</b><span class="tnum" data-count="${LEAD.s}" data-delay="900">${money(LEAD.s)}</span></div>
          <div class="f"><b>Sales</b><span class="tnum">${LEAD.sales}</span></div><div class="f"><b>Ahead by</b><span class="tnum" data-count="${ahead}" data-delay="1100">${money(ahead)}</span></div></div></div>
      <div class="stub"><div><div class="kicker">Seat</div><div class="seat">1A</div></div><div><div class="kicker">To DXB · Dubai</div><div class="code" aria-hidden="true"></div></div></div></div>
    <div class="abs l1-rest" style="width:auto; right:48px"><span class="k">Also on board</span>${restChips(M, 10, M.scored.length)}${M.yet.length ? `<span class="more" style="--i:12">+${M.yet.length} yet to score</span>` : ''}</div>
    <div class="abs l1-foot">${legendHTML()}</div>`;
  const loop = startLoop();
  /* [DUBAI-TV 2026-10-19, user] the bigger "Also on board" chips still keep to their two lines above the legend: a chip that
     would start a third line is left out and counted ("+N more"). Measured again once the board's fonts are in. */
  const restTotal = Math.max(0, M.scored.length - 10);
  const fitRest = (): void => {
    const box = el.querySelector<HTMLElement>('.l1-rest');
    const first = box?.querySelector<HTMLElement>('.chip');
    if (!box || !first) return;
    const max = first.offsetHeight * 2 + 8 + 2;
    let chips = Array.from(box.querySelectorAll<HTMLElement>('.chip'));
    while (box.offsetHeight > max && chips.length) {
      chips[chips.length - 1].remove();
      chips = Array.from(box.querySelectorAll<HTMLElement>('.chip'));
      let more = box.querySelector<HTMLElement>('.more');
      if (!more) { more = document.createElement('span'); more.className = 'more'; box.appendChild(more); }
      const hidden = restTotal - chips.length;
      more.textContent = [`+${hidden} more`, M.yet.length ? `+${M.yet.length} yet to score` : ''].filter(Boolean).join(' · ');
    }
  };
  /* [DUBAI-REPLAY 2026-10-19, user] "every 30 seconds this should show the animation that i get at the page start": the page
     is painted again — the planes take off from the start, the departures board flips in, the figures count up. */
  const paint = (): void => {
    el.innerHTML = html;
    fitRest();
    if (document.fonts) void document.fonts.ready.then(() => { if (el.isConnected) fitRest(); });
    const refs = PL.map(({ i }) => {
      const g = el.querySelector<SVGGElement>(`#l1pl${i}`) as SVGGElement;
      return { g, rot: g.querySelector('.rot') as SVGGElement, tag: g.querySelector('.tag') as SVGGElement };
    });
    const place = (i: number, tt: number): void => {
      const [px, py] = at(tt); const ang = slope(tt); const lane = LANES[i] * Math.min(1, tt / 0.5);
      refs[i].g.setAttribute('transform', `translate(${(px - Math.sin(ang) * lane).toFixed(1)} ${(py + Math.cos(ang) * lane).toFixed(1)})`);
      refs[i].rot.setAttribute('transform', `rotate(${((ang * 180) / Math.PI + 90).toFixed(1)})`);
      refs[i].tag.setAttribute('transform', `translate(${(-Math.cos(ang) * 24).toFixed(1)} ${(-Math.sin(ang) * 24).toFixed(1)})`);
    };
    PL.forEach(({ i, target }) => {
      place(i, target);
      if (REDUCED) return;
      let start = 0;
      loop.add((now) => {
        if (!start) start = now;
        const p = clamp01((now - start - 250 - i * 160) / 2600);
        place(i, 0.02 + (target - 0.02) * easeInOut(p));
        return p >= 1;
      });
    });
    countUp(el, loop);
    if (!REDUCED) {
      loop.add(flapFrame(flapCells(el, '.l1-board [data-text]'), 300, true));
      loop.add(flapFrame(flapCells(el, '.l1-count [data-text]'), 150, true));
    }
  };
  paint();
  if (!REDUCED) loop.every(30000, paint);
  clock(el, loop);
  return loop;
}

/* 2 · Scorecard — the flight manifest: how every score is made */
function scorecard(el: HTMLElement, M: Model): Loop {
  const cellv = (v: number): string => (v > 0 ? `<span class="n">${money(v)}</span>` : '<span class="n dim">—</span>');
  const rows = M.people.map((p) => `<div class="r flip${p.rank === 1 && p.s > 0 ? ' lead' : ''}${p.rank <= 3 && p.s > 0 ? ' top' : ''}${p.s <= 0 ? ' zero' : ''}" style="--fd:${150 + (p.rank - 1) * 32}ms">
      <span class="rk">${p.s > 0 ? p.rank : '–'}</span><span class="nm">${esc(p.n)}</span><span class="tm">${esc(p.t)}</span><span class="n">${p.sales}</span>
      ${cellv(p.m.c)}${cellv(p.m.p)}${cellv(p.m.f)}${cellv(p.m.x)}${cellv(p.m.s)}${p.refunds > 0 ? `<span class="n rf">−${money(p.refunds)}</span>` : '<span class="n dim">—</span>'}
      <span class="sc"><span data-text="${money(p.s)}" data-row="${p.rank}" data-col="0">${money(p.s)}</span></span></div>`).join('');
  const W = cwOf(el);
  /* [DUBAI-TV 2026-10-19, user] "someone can easily read all the text": every row is as tall, and its text as big, as the room
     between the header and the foot allows (rows up to 28 px, text up to 17.5 px) — so a longer list shrinks a little and
     never runs into the foot. */
  const pitch = Math.min(28, 632 / Math.max(1, M.people.length));
  const rowH = r2(pitch - 1);
  const rowFont = r2(Math.max(12, Math.min(17.5, rowH * 0.68)));
  const html = `${sky(W)}${pageHead('Scorecard', M.take.scorecard)}
    <div class="abs l1-tiles" style="left:${700 + (W - 1600) / 2}px">
      <div class="l1-tile" style="--d:.15s"><b>Points so far</b><span class="tnum" data-count="${M.total}">${money(M.total)}</span></div>
      <div class="l1-tile" style="--d:.25s"><b>Deals counted</b><span class="tnum">${M.deals.length}</span></div>
      <div class="l1-tile" style="--d:.35s"><b>With 2nd voice</b><span class="tnum">${M.assisted.length}</span></div></div>
    ${info()}
    <div class="abs l1-sc" style="width:auto; right:48px; --scr:${rowH}px; --scf:${rowFont}px"><div class="hdr"><span>#</span><span>Name</span><span>Team</span><span class="n">Sales</span>
        <span class="n">Company lead <i>100%</i></span><span class="n">Past client <i>80%</i></span><span class="n">Conference <i>50%</i></span><span class="n">No source <i>100%</i></span><span class="n">2nd voice <i>30%</i></span><span class="n">Refund / dispute</span><span class="n">Score</span></div>${rows}</div>
    ${pageFoot(M)}`;
  const loop = startLoop();
  // [DUBAI-REPLAY 2026-10-19, user] "scoreboard will show the animation for this every 30 seconds": painted again — the rows
  // flip in, the scores flap, the tiles count up.
  const paint = (): void => {
    el.innerHTML = html;
    countUp(el, loop);
    if (!REDUCED) {
      const cells = flapCells(el, '.l1-sc .sc [data-text]').map((c) => ({ ...c, col: 0, row: c.row * 0.6 }));
      loop.add(flapFrame(cells, 500, true));
    }
  };
  paint();
  if (!REDUCED) loop.every(30000, paint);
  clock(el, loop);
  return loop;
}

/* 3 · The Chase — final approach: who is closest to #1, and the one deal that moves each person up */
function chase(el: HTMLElement, M: Model): Loop {
  const LEAD = M.lead;
  if (!LEAD) return empty(el, M, 'The Chase');
  const W = cwOf(el); const svgW = W - 96; // [DUBAI-FILL] the runway runs edge to edge (1504 on a 16:9 screen)
  const X0 = 70; const X1 = svgW - 124; const Y = 150;
  const xOf = (s: number): number => X0 + (X1 - X0) * (Math.max(0, s) / LEAD.s);
  const ticks = [0, 0.25, 0.5, 0.75].map((f) => `<text class="tick" x="${(X0 + (X1 - X0) * f).toFixed(1)}" y="${Y + 24}" text-anchor="middle">${money(LEAD.s * f, false)}</text>`).join('');
  /* [DUBAI-GATES 2026-10-19, user] "scoreboard already has a table … i dont want the same in the chase … it will say them for
     the rank 1, what they need to do for the rank 1": the table is gone. Everyone behind #1 — those yet to score included —
     gets a boarding-gate card: the gap to #1, how far they have come next to the leader, and the one deal that takes #1
     (a deal must be OVER the gap: a tie stays with the one who has more sales). Ten cards at a time; the next ten flap in
     like the departures board every 20 s ([DUBAI-CHASE-20]), so a one-minute visit shows every person. */
  const chasers = M.people.slice(1);
  const PER = 10;
  const views = Math.max(1, Math.ceil(chasers.length / PER));
  // [DUBAI-CHASE-20] 20 s per group (3 groups = the minute); more groups share the minute evenly
  const step = Math.min(CHASE_STEP_MS, Math.floor(CHASE_VISIT_MS / views));
  const gate = (p: Ranked, k: number): string => {
    const gap = r2(LEAD.s - p.s);
    const pct = Math.max(0, Math.min(100, (p.s / LEAD.s) * 100));
    const how = gap <= 0
      ? '<span>Level with #1:</span><span><em>any new deal</em> takes it</span>'
      : `<span>Company lead over <em>${money(gap)}</em></span><span>or past client over <em>${money(r2(gap / 0.8))}</em></span>`;
    return `<div class="gate flip" style="--fd:${120 + k * 60}ms">
      <div class="top"><span class="rk">${p.s > 0 ? p.rank : '–'}</span><span class="nm">${esc(p.n)}</span><span class="tm">${esc(p.t)}</span></div>
      <div class="need"><b class="tnum">${money(gap)}</b>to #1</div>
      <div class="bar"><i style="width:${pct.toFixed(1)}%"></i></div>
      <div class="how">${how}</div></div>`;
  };
  const view = (v: number): string => chasers.slice(v * PER, (v + 1) * PER).map(gate).join('');
  const range = (v: number): string => {
    const g = chasers.slice(v * PER, (v + 1) * PER);
    return g.length ? `Ranks ${g[0].rank}–${g[g.length - 1].rank} of ${M.people.length}` : '';
  };
  /* [DUBAI-CHASE-20] Which group: where this visit's clock stands — the first group when a visit starts, the same group (and
     the same timing) when the page is only drawn again, the group it was on when a new deal interrupted the visit. */
  if (chaseFresh) { chaseShown = 0; chaseFresh = false; }
  chaseFrom = Date.now();
  let v = Math.floor(chaseShown / step) % views;
  /* [DUBAI-CHASE-SYNC 2026-10-19, user] "it should have all the names which are in the below cards": the runway carries #1 at
     the gate AND exactly the people on the cards below it — when the cards flip to the next ten, the planes change with them.
     Labels sit in TWO rows (above / below the runway), neighbours on opposite rows, slid sideways until none overlap (scores
     bunch up low down: #17-#19 are within $3 of each other today); a leader line joins every label to its plane. */
  const fleet = (v: number): string => [LEAD, ...chasers.slice(v * PER, (v + 1) * PER)].map((p, i) => {
    const lead = i === 0;
    const x = xOf(p.s).toFixed(1);
    return `<g class="pl" data-x="${x}" data-i="${i}" transform="translate(${X0} ${Y})"><g class="bob" style="animation-delay:${(i * 0.4).toFixed(2)}s"><g transform="rotate(90)"><line class="trail" x1="0" y1="13" x2="0" y2="80" stroke="url(#${lead ? 'l1trailLead' : 'l1trail'})"></line><path class="plane${lead ? ' lead' : ''}" transform="scale(1.25)" d="${PLANE}"></path></g></g></g>
      <g class="labg" data-x="${x}" style="animation: dxb-fadeUp .5s ease-out ${(1.6 + i * 0.1).toFixed(2)}s both">
        <line class="lead-line" x1="${x}" y1="${Y}" x2="${x}" y2="${Y}"></line>
        <text class="lab${lead ? ' lead' : ''}" x="${x}" y="${Y}" text-anchor="middle">${p.s > 0 ? p.rank : '–'} · ${esc(p.n)}</text>
        <text class="labs${lead ? ' lead' : ''}" x="${x}" y="${Y}" text-anchor="middle">${lead ? `${money(p.s)} · at the gate` : `${money(p.s)} · −${money(LEAD.s - p.s)}`}</text></g>`;
  }).join('');
  // [DUBAI-TV] 21 px between a label's name and its figures (the bigger TV sizes need the room)
  const ROWS = [
    { name: Y - 95, score: Y - 74, from: Y - 14, to: Y - 68 },  // above the runway
    { name: Y + 74, score: Y + 95, from: Y + 14, to: Y + 58 },  // below it
  ];
  /** Measure every label, split them into the two rows (right to left: #1 on the top row, then alternating), and slide
   *  each row's labels apart until none overlap, keeping each as close above / below its plane as the row allows. */
  const place = (root: Element): void => {
    const items = Array.from(root.querySelectorAll<SVGGElement>('.labg')).map((g) => {
      let w = 0;
      g.querySelectorAll<SVGTextElement>('text').forEach((t) => {
        try { w = Math.max(w, t.getComputedTextLength()); } catch { w = Math.max(w, 180); }
      });
      return { g, x: Number(g.dataset.x), w: w || 180, c: 0 };
    }).sort((a, b) => b.x - a.x);
    const GAP = 18;
    [items.filter((_, k) => k % 2 === 0), items.filter((_, k) => k % 2 === 1)].forEach((row, r) => {
      const lane = ROWS[r];
      row.sort((a, b) => a.x - b.x);
      row.forEach((it, k) => {
        it.c = Math.max(it.x, it.w / 2 + 4);
        if (k) { const prev = row[k - 1]; it.c = Math.max(it.c, prev.c + (prev.w + it.w) / 2 + GAP); }
      });
      for (let k = row.length - 1; k >= 0; k -= 1) {
        const it = row[k];
        const max = k === row.length - 1 ? svgW - it.w / 2 - 4 : row[k + 1].c - (row[k + 1].w + it.w) / 2 - GAP;
        if (it.c > max) it.c = max;
      }
      row.forEach((it) => {
        const texts = it.g.querySelectorAll('text');
        texts[0].setAttribute('x', it.c.toFixed(1)); texts[0].setAttribute('y', String(lane.name));
        texts[1].setAttribute('x', it.c.toFixed(1)); texts[1].setAttribute('y', String(lane.score));
        const ln = it.g.querySelector('line');
        if (ln) {
          ln.setAttribute('x1', it.x.toFixed(1)); ln.setAttribute('y1', String(lane.from));
          ln.setAttribute('x2', it.c.toFixed(1)); ln.setAttribute('y2', String(lane.to));
        }
      });
    });
  };
  el.innerHTML = `${sky(W)}${pageHead('The Chase', M.take.chase)}${info()}
    <svg class="abs l1-ch" viewBox="0 0 ${svgW} 292" style="width:${svgW}px" aria-hidden="true">${trailDefs}
      <line class="rwy-bg" x1="${X0}" y1="${Y}" x2="${X1}" y2="${Y}"></line><line class="rwy" x1="${X0}" y1="${Y}" x2="${X1 + 40}" y2="${Y}"></line>${ticks}
      <text class="tick" x="${X1 + 70}" y="${Y + 24}" text-anchor="middle">#1</text>
      <path class="burj" transform="translate(${X1 + 70} ${Y - 62})" d="M0 0 L2 16 L4 28 L6 38 L8 47 L10 54 L-10 54 L-8 47 L-6 38 L-4 28 L-2 16 Z"></path>
      <circle class="burj-light" cx="${X1 + 70}" cy="${Y - 62}" r="2.6"></circle>
      <circle class="pulse" cx="${X1 + 70}" cy="${Y}" r="10"></circle><circle class="pulse p2" cx="${X1 + 70}" cy="${Y}" r="10"></circle>
      <g data-fleet>${fleet(v)}</g></svg>
    <div class="abs l1-gates" style="width:auto; right:48px"><div class="gh"><span>What it takes to be #1 · one deal</span><span class="tnum" data-gp>${range(v)}</span></div>
      <div class="grid" data-grid>${view(v)}</div></div>
    ${pageFoot(M)}`;
  const loop = startLoop();
  const root = el.querySelector('[data-fleet]') as Element;
  /** The planes fly in from the start of the runway to their place. */
  const fly = (): void => {
    root.querySelectorAll<SVGGElement>('.pl').forEach((g) => {
      const x = Number(g.dataset.x); const i = Number(g.dataset.i);
      const set = (px: number): void => g.setAttribute('transform', `translate(${px.toFixed(1)} ${Y})`);
      set(x);
      if (REDUCED) return;
      let start = 0;
      loop.add((now) => {
        if (!start) start = now;
        const p = clamp01((now - start - 200 - i * 120) / 2200);
        set(X0 + (x - X0) * easeInOut(p));
        return p >= 1;
      });
    });
  };
  place(root);
  fly();
  // [DUBAI-CHASE-20] every step: the next group (or the same one again when there is only one) — cards AND runway, animated;
  // the first change comes when this visit's clock reaches the next step
  if (!REDUCED || views > 1) {
    const grid = el.querySelector<HTMLElement>('[data-grid]');
    const label = el.querySelector<HTMLElement>('[data-gp]');
    const next = (): void => {
      v = (v + 1) % views;
      if (grid) grid.innerHTML = view(v);
      if (label) label.textContent = range(v);
      root.innerHTML = fleet(v);
      place(root);
      fly();
    };
    loop.after(step - (chaseShown % step), () => { next(); loop.every(step, next); });
  }
  clock(el, loop);
  // [DUBAI-CHASE-20] when this drawing ends (the page leaves, a new deal takes the screen, or it is drawn again) its time is
  // added to the visit's clock
  const stop = loop.stop.bind(loop);
  loop.stop = (): void => {
    if (chaseFrom) { chaseShown += Date.now() - chaseFrom; chaseFrom = 0; }
    stop();
  };
  return loop;
}

/* 4 · Second Voice — co-pilots: who helped close deals, and how each deal was split */
function voice(el: HTMLElement, M: Model): Loop {
  // [DUBAI-TV] eight cards fill the column down to the foot; a ninth helper would run into it
  const helpers = M.helpers.slice(0, 8).map((p, i) => `<div class="h${i === 0 ? ' lead' : ''}" style="--i:${i}"><span class="rk">${i + 1}</span>
      <span class="nm">${esc(p.n)}<small>Helped on ${plural(p.assists, 'deal', 'deals')} · ${esc(p.t)}</small></span><span class="pt tnum">${money(p.m.s)}</span></div>`).join('');
  const rowsHtml = M.assisted.map((d) => `<div class="d"><div class="who">${esc(d.owner)}<span class="arr">→</span>${d.voices.map((v) => `<span class="vc">${esc(v.n)}</span>`).join(' · ')}
        <small>${esc(d.when)} · ${esc(d.site)} · ${esc(d.src)} ${Math.round(d.w * 100)}%</small></div>
      <div class="v tnum"><small>Deal points</small>${money(d.pts)}</div><div class="v tnum"><small>Owner 70%</small>${money(d.ownerPts)}</div>
      <div class="v s tnum"><small>Second voice 30%</small>${d.voices.map((v) => (v.out ? '<span class="out">not in contest</span>' : money(v.pts))).join(' · ')}</div></div>`).join('');
  el.innerHTML = `${sky(cwOf(el))}${pageHead('Second Voice', M.take.voice)}${info()}
    <div class="abs l1-svl"><h3>Top second voices</h3>${helpers}</div>
    <div class="abs l1-svd" style="width:auto; right:48px"><h3>Every deal with a second voice · ${M.assisted.length} of ${M.deals.length}</h3><div class="scroller"><div class="track">${rowsHtml}<div class="dup">${rowsHtml}</div></div></div></div>
    <div class="abs l1-pf"><span>The owner gets 70% and the second voice 30% (shared when there are two). Only the owner gets the sale.</span></div>`;
  const loop = startLoop();
  startRoll(el.querySelector<HTMLElement>('.scroller'), 22);
  clock(el, loop);
  return loop;
}

/* 5 · New Deal — the celebration: GIF behind, boarding pass in front */
function deal(el: HTMLElement, M: Model, o: DealOpts): Loop {
  const d = o.deal;
  if (!d) return empty(el, M, 'New Deal');
  const owner = M.byName.get(d.owner);
  const split = d.voices.length
    ? `${d.owner} ${money(d.ownerPts)} (70%) · ${d.voices.map((v) => `${v.n} ${v.out ? 'not in the contest' : money(v.pts)}`).join(' · ')} (30%)`
    : 'Self closed: the owner keeps 100%';
  // [DUBAI-COUNT-BY-SITE 2026-10-26] repeat = the same website again: the money counts, the sale count stays (one per website)
  const note = d.repeat ? `Same website again: the money counts, still one sale · ${split}` : split;
  const eyebrow = o.test ? 'Test · the latest deal again' : o.fresh ? 'New deal' : 'Latest deal';
  const W = cwOf(el);
  el.innerHTML = `<div class="gifbg l1-nd">${o.gifUrl ? `<img alt="" src="${esc(o.gifUrl)}">` : ''}<div class="scrim"></div></div>
    <div class="abs l1-ndjet" style="--jet-end:${W + 100}px" aria-hidden="true"><svg width="220" height="30" viewBox="0 0 220 30"><defs><linearGradient id="l1jt" x1="0" x2="1"><stop offset="0" stop-color="#ffb547" stop-opacity="0"></stop><stop offset="1" stop-color="#ffb547" stop-opacity=".8"></stop></linearGradient></defs>
      <rect x="0" y="14" width="170" height="3" fill="url(#l1jt)"></rect><g transform="translate(195 15) rotate(90) scale(1.7)"><path fill="#ffb547" d="${PLANE}"></path></g></svg></div>
    <div class="abs l1-ndtop"><div class="eyebrow">${eyebrow}</div>
      <div class="big">${o.fresh ? 'New deal closed' : 'The latest deal'}</div><div class="when">${esc(d.when)} IST</div></div>
    <div class="abs l1-ndc" style="left:${(W - 1140) / 2}px"><div class="main"><div class="kicker">Boarding pass · next stop DXB</div>
        <div><div class="pax">${esc(d.owner)}</div><div class="site">${esc(d.site)}</div></div>
        <div class="fields"><div class="f"><b>Deal</b><span class="tnum">${money(d.amount)}</span></div>
          <div class="f"><b>Source</b><span>${esc(d.src)} ${Math.round(d.w * 100)}%</span></div>
          <div class="f"><b>Points</b><span class="pts tnum" data-count="${d.pts}" data-pre="+" data-delay="700">+${money(d.pts)}</span></div></div>
        <div class="split">${esc(note)}</div></div>
      <div class="stub"><div><div class="kicker">Now</div><div class="now">#${owner && owner.s > 0 ? owner.rank : '–'}</div></div>
        <div><div class="kicker">Total</div><div class="tot tnum" data-count="${owner ? owner.s : 0}" data-delay="900">${money(owner ? owner.s : 0)}</div></div><div class="code" aria-hidden="true"></div></div></div>
    <div class="abs l1-pf">${legendHTML()}</div>`;
  const img = el.querySelector<HTMLImageElement>('.gifbg img');
  if (img) img.addEventListener('error', () => img.remove());
  const loop = startLoop();
  countUp(el, loop);
  return loop;
}

export const FLIGHT_PAGES: Record<Exclude<PageId, 'deal'>, (el: HTMLElement, M: Model) => Loop> = { standings, scorecard, chase, voice };
export const renderDealPage = deal;
